import { useEffect, useRef, useState } from "react";

/**
 * useAutoSave — debounces + fires a save function every `intervalMs` when the
 * tracked form object changes, and marks a `status` state as one of:
 *   idle · saving · saved · error
 *
 * @param {any}   form           — the form object to watch
 * @param {func}  saveFn         — async () => void. Called whenever a save should fire.
 * @param {number} intervalMs    — debounce/save interval. Default 20s.
 * @param {bool}  enabled        — set to false to pause auto-save (e.g. while loading).
 */
export default function useAutoSave(form, saveFn, intervalMs = 20000, enabled = true) {
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const timerRef = useRef(null);
  const dirtyRef = useRef(false);
  const initialRef = useRef(true);

  useEffect(() => {
    if (!enabled) return;
    // Skip the very first render so we don't save an empty form immediately.
    if (initialRef.current) { initialRef.current = false; return; }
    dirtyRef.current = true;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      if (!dirtyRef.current) return;
      dirtyRef.current = false;
      setStatus("saving");
      try {
        await saveFn();
        setStatus("saved");
        setLastSavedAt(new Date());
      } catch (e) {
        setStatus("error");
      }
    }, intervalMs);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(form), enabled]);

  const saveNow = async () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setStatus("saving");
    try {
      await saveFn();
      setStatus("saved");
      setLastSavedAt(new Date());
      dirtyRef.current = false;
    } catch (e) {
      setStatus("error");
      throw e;
    }
  };

  return { status, lastSavedAt, saveNow };
}
